import { useRef, useState } from "react";
import { useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { RailQueryParams } from "../query.types";
import { RailResponseDTO } from "../response.types";
import { PaymentsModalType, SubscriptionDataModel } from "entities/shared/subscriptionModel";
import { OnErrorCallback, OnSettledCallback, OnSuccessCallback, useRequestCallbacks } from "shared/api/lib/api.hooks";
import { useAppDispatch } from "shared/lib/redux";
import { PartialBy } from "shared/lib/typeCheck.lib";

import { RailSearchCardModel } from "../../model/model";
import { railSearchService } from "../api";

type RequestLimitReachedCallbackFunc = () => void;
type SetSearchCacheFunc = (data: RailResponseDTO | undefined) => void;
type ResetSearchQueryFunc = () => Promise<void>;
type OnSuccessCallbackFunc = OnSuccessCallback<RailSearchCardModel>;

type SearchRequestFuncOptions = {
  onSuccess?: OnSuccessCallbackFunc;
  onError?: OnErrorCallback<SearchError>;
  onSettled?: OnSettledCallback<RailSearchCardModel, SearchError>;
  resetQueryBeforeRequest?: boolean;
  onAppIntegrationRequestLimitReached?: RequestLimitReachedCallbackFunc;
  onWebAppRequestLimitReached?: RequestLimitReachedCallbackFunc;
};

export type RailSearchRequestFunc = (params: RailQueryParams, options?: SearchRequestFuncOptions) => void;

export type UseSearchQueryReturnType = {
  searchRequest: RailSearchRequestFunc;
  resetSearchQuery: ResetSearchQueryFunc;
  removeSearchCache: () => void;
  setSearchCache: SetSearchCacheFunc;
  cancelSearchRequest: () => Promise<void>;
  searchResponse: RailSearchCardModel | undefined;
  isSearchSuccess: boolean;
  isSearchError: boolean;
  isSearchLoading: boolean;
  isSearchFetching: boolean;
};

type SearchError = Error;
type QueryParams = Pick<PartialBy<UseQueryOptions, "queryKey">, "queryKey"> & {
  isAuthenticated: boolean;
  setSubscription: (subscription: SubscriptionDataModel) => {
    type: string;
    payload: SubscriptionDataModel | null;
  };
  setActivePaymentModal: (activePaymentModal: PaymentsModalType) => {
    type: string;
    payload: PaymentsModalType | null;
  };
  setAppIntegrationRequestLimitReached: (appIntegrationRequestLimitReached: boolean) => {
    type: string;
    payload: boolean | null;
  };
  setWebAppRequestLimitReached: (webAppRequestLimitReached: boolean) => {
    type: string;
    payload: boolean | null;
  };
};

const RAIL_SEARCH_CACHE_KEY = "search-rail-tracking-query";

export const useSearchQuery = ({
  queryKey = [RAIL_SEARCH_CACHE_KEY],
  isAuthenticated,
  setSubscription,
  setActivePaymentModal,
  setAppIntegrationRequestLimitReached,
  setWebAppRequestLimitReached,
}: QueryParams): UseSearchQueryReturnType => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const [searchParams, setSearchParams] = useState<RailQueryParams>();
  const [enabled, setEnabled] = useState(false);

  const appIntegrationRequestLimitReachedCallbackRef = useRef<RequestLimitReachedCallbackFunc | undefined>();
  const webAppRequestLimitReachedCallbackRef = useRef<RequestLimitReachedCallbackFunc | undefined>();

  const meta = useQuery<RailResponseDTO, SearchError, RailSearchCardModel>({
    queryKey,
    queryFn: searchParams ? ({ signal }) => railSearchService.search(searchParams, signal) : undefined,
    select: (data) => new RailSearchCardModel(data),
    enabled,
  });

  const { data, isError, isLoading, isSuccess, isFetching } = meta;

  const { setRequestCallbacks } = useRequestCallbacks<RailSearchCardModel, SearchError>(meta);

  const removeSearchCache = () => {
    queryClient.removeQueries({ queryKey });
  };

  const resetSearchQuery: ResetSearchQueryFunc = () => {
    return queryClient.resetQueries({ queryKey });
  };

  const setSearchCache: SetSearchCacheFunc = (data) => {
    queryClient.setQueryData(queryKey, data);
  };

  const searchRequest: RailSearchRequestFunc = (
    params,
    {
      onSuccess,
      onError,
      onSettled,
      resetQueryBeforeRequest = true,
      onAppIntegrationRequestLimitReached,
      onWebAppRequestLimitReached,
    } = {}
  ) => {
    setRequestCallbacks({
      onSuccess,
      onError,
      onSettled: (response, error) => {
        // TODO: Implement DataLayersManager
        // const dlm = new DataLayersManager();
        const subscription = response?.subscription;
        if (subscription) {
          dispatch(setSubscription(subscription));
        }

        if (response?.requestLimitReachedAppIntegration) {
          dispatch(setAppIntegrationRequestLimitReached(true));
          appIntegrationRequestLimitReachedCallbackRef.current?.();
        }
        if (response?.requestLimitReachedWebApp) {
          if (isAuthenticated) {
            if (subscription?.isFree) {
              dispatch(setActivePaymentModal(PaymentsModalType.FREE));
              // dlm.setModel(response).searchLimitReached();
            } else {
              dispatch(setActivePaymentModal(PaymentsModalType.PAY));
            }
          } else {
            dispatch(setWebAppRequestLimitReached(true));
          }
          webAppRequestLimitReachedCallbackRef.current?.();
        }
        onSettled?.(response, error);
        setEnabled(false);
      },
    });

    appIntegrationRequestLimitReachedCallbackRef.current = onAppIntegrationRequestLimitReached;
    webAppRequestLimitReachedCallbackRef.current = onWebAppRequestLimitReached;

    if (resetQueryBeforeRequest) {
      resetSearchQuery().finally(() => {
        setSearchParams(params);
        setEnabled(true);
      });
      return;
    }

    setSearchParams(params);
    setEnabled(true);
  };

  const cancelSearchRequest = () => {
    return queryClient.cancelQueries({ queryKey });
  };

  return {
    searchRequest,
    resetSearchQuery,
    removeSearchCache,
    setSearchCache,
    searchResponse: data,
    isSearchSuccess: isSuccess,
    isSearchError: isError,
    isSearchLoading: isLoading,
    isSearchFetching: isFetching,
    cancelSearchRequest,
  };
};
